/**
 * A five-field cron expression in words ("Weekdays at 11:25"), for the triggers
 * dialog. Covers the shapes people actually write — a fixed time on some days, every
 * hour, every N minutes, a day of the month — and returns null for anything else,
 * where the dialog shows the expression alone rather than a guess.
 */

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const isNumber = (field: string) => /^\d+$/.test(field);
const pad = (value: number) => String(value).padStart(2, "0");

/** The days of the week a field names, 0 = Sunday (7 folds onto it), or null if it is not a plain list/range. */
function weekdays(field: string): number[] | null {
  const days = new Set<number>();
  for (const part of field.split(",")) {
    const range = /^(\d)(?:-(\d))?$/.exec(part);
    if (!range) return null;
    const from = Number(range[1]);
    const to = range[2] === undefined ? from : Number(range[2]);
    if (from > 7 || to > 7 || to < from) return null;
    for (let day = from; day <= to; day++) days.add(day % 7);
  }
  return [...days].sort((a, b) => a - b);
}

function describeDays(dom: string, month: string, dow: string): string | null {
  if (month !== "*") return null;
  if (dom !== "*") {
    if (dow !== "*" || !isNumber(dom)) return null;
    return `On day ${Number(dom)} of every month`;
  }
  if (dow === "*") return "Every day";
  const days = weekdays(dow);
  if (!days || days.length === 0) return null;
  const key = days.join(",");
  if (key === "1,2,3,4,5") return "Weekdays";
  if (key === "0,6") return "Weekends";
  if (days.length === 7) return "Every day";
  if (days.length === 1) return `${DAY_NAMES[days[0]!]}s`;
  // A run of consecutive days reads as a range, anything else as a list.
  const consecutive = days.every((day, index) => index === 0 || day === days[index - 1]! + 1);
  return consecutive && days.length > 2
    ? `${DAY_SHORT[days[0]!]}–${DAY_SHORT[days[days.length - 1]!]}`
    : days.map((day) => DAY_SHORT[day]).join(", ");
}

export function describeCron(expression: string | null | undefined): string | null {
  const fields = (expression ?? "").trim().split(/\s+/);
  if (fields.length !== 5) return null;
  const [minute, hour, dom, month, dow] = fields as [string, string, string, string, string];
  const days = describeDays(dom, month, dow);
  if (!days) return null;
  // After an interval the days read as a tail: "Every hour at :00, weekdays". Day names keep their capital.
  const on = days === "Every day" ? "" : `, ${/^(Weekdays|Weekends|On day)/.test(days) ? days[0]!.toLowerCase() + days.slice(1) : days}`;

  if (minute === "*" && hour === "*") return `Every minute${on}`;
  const step = /^\*\/(\d+)$/.exec(minute);
  if (step && hour === "*") return `Every ${Number(step[1])} minutes${on}`;
  if (isNumber(minute) && hour === "*") return `Every hour at :${pad(Number(minute))}${on}`;
  if (isNumber(minute) && /^\d+(,\d+)*$/.test(hour)) {
    const times = hour.split(",").map((h) => `${pad(Number(h))}:${pad(Number(minute))}`);
    const at = times.length === 1 ? times[0] : `${times.slice(0, -1).join(", ")} and ${times[times.length - 1]}`;
    return `${days} at ${at}`;
  }
  return null;
}

/** An instant as wall-clock time in a given zone: "Wed 30 Sep, 11:25". */
export function formatInZone(iso: string, zone: string): string {
  try {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat("en-US", {
        timeZone: zone,
        weekday: "short",
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
      })
        .formatToParts(new Date(iso))
        .map((part) => [part.type, part.value]),
    );
    return `${parts.weekday} ${parts.day} ${parts.month}, ${parts.hour}:${parts.minute}`;
  } catch {
    return iso;
  }
}
